// Which GST applies to a sale: CGST + SGST when the seller and the place of
// supply are in the same state, IGST when they are in different states, none
// for an export / zero-rated sale. Shared by the API (when it creates a
// proforma) and the web app (when it builds a proforma preview) so both reach
// the same answer.

export type GstMode = 'intra' | 'inter' | 'none'

// GST state codes (the first two digits of a GSTIN).
const STATE_NAMES_BY_CODE: Record<string, string[]> = {
  '01': ['jammu and kashmir', 'jammu kashmir'],
  '02': ['himachal pradesh'],
  '03': ['punjab'],
  '04': ['chandigarh'],
  '05': ['uttarakhand', 'uttaranchal'],
  '06': ['haryana'],
  '07': ['delhi', 'new delhi', 'nct of delhi'],
  '08': ['rajasthan'],
  '09': ['uttar pradesh'],
  '10': ['bihar'],
  '11': ['sikkim'],
  '12': ['arunachal pradesh'],
  '13': ['nagaland'],
  '14': ['manipur'],
  '15': ['mizoram'],
  '16': ['tripura'],
  '17': ['meghalaya'],
  '18': ['assam'],
  '19': ['west bengal'],
  '20': ['jharkhand'],
  '21': ['odisha', 'orissa'],
  '22': ['chhattisgarh'],
  '23': ['madhya pradesh'],
  '24': ['gujarat'],
  '26': ['dadra and nagar haveli and daman and diu', 'dadra and nagar haveli', 'daman and diu'],
  '27': ['maharashtra'],
  '29': ['karnataka'],
  '30': ['goa'],
  '31': ['lakshadweep'],
  '32': ['kerala'],
  '33': ['tamil nadu'],
  '34': ['puducherry', 'pondicherry'],
  '35': ['andaman and nicobar islands', 'andaman and nicobar'],
  '36': ['telangana', 'telengana'],
  '37': ['andhra pradesh'],
  '38': ['ladakh'],
  '97': ['other territory'],
}

const titleCase = (text: string) => text.replace(/\b([a-z])/g, (c) => c.toUpperCase()).replace(/\bAnd\b/g, 'and')

/** Every GST state / UT as { code, name, label } with label like "Maharashtra (27)", ordered by code. */
export const GST_STATES: Array<{ code: string; name: string; label: string }> = Object.entries(STATE_NAMES_BY_CODE)
  .map(([code, names]) => {
    const name = titleCase(names[0])
    return { code, name, label: `${name} (${code})` }
  })
  .sort((a, b) => a.name.localeCompare(b.name))

/** "Maharashtra (27)" for a GST state code; '' when the code is not a known state. */
export function stateLabelFromCode(code?: string | null): string {
  const hit = GST_STATES.find((s) => s.code === String(code ?? '').trim())
  return hit ? hit.label : ''
}

const CODE_BY_STATE_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_NAMES_BY_CODE).flatMap(([code, names]) => names.map((name) => [name, code]))
)

const normalizeName = (text: string) =>
  text
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\(\s*\d{2}\s*\)/g, ' ')
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

/** The GST state code in a state name ("Maharashtra", "Maharashtra (27)", "27 - Maharashtra"); null when it is not recognised. */
export function stateCodeFromText(text?: string | null): string | null {
  const value = String(text ?? '').trim()
  if (!value) return null
  const inBrackets = value.match(/\(\s*(\d{2})\s*\)/)
  if (inBrackets) return inBrackets[1]
  const byName = CODE_BY_STATE_NAME[normalizeName(value)]
  if (byName) return byName
  const leading = value.match(/^(\d{2})\b/)
  return leading ? leading[1] : null
}

/** The state code at the start of a 15-character GSTIN; null when it is not one. */
export function stateCodeFromGstin(gstin?: string | null): string | null {
  const value = String(gstin ?? '').trim().toUpperCase()
  return /^\d{2}[A-Z0-9]{13}$/.test(value) ? value.slice(0, 2) : null
}

export interface PlaceOfSupply {
  /** GST state code (e.g. "27"), or null when the place of supply is not known. */
  code: string | null
  /** "Maharashtra (27)", or '' when unknown. */
  label: string
  /** Where it came from: the customer's GSTIN, the State field, or nowhere. */
  source: 'gstin' | 'state' | 'none'
}

/**
 * The place of supply of a sale to a registered or unregistered customer: the state in the customer's
 * GSTIN when there is one, otherwise the State field. No default — an unknown place is reported as unknown.
 */
export function resolvePlaceOfSupply(input: { customerGstin?: string | null; customerState?: string | null }): PlaceOfSupply {
  const fromGstin = stateCodeFromGstin(input.customerGstin)
  if (fromGstin) return { code: fromGstin, label: stateLabelFromCode(fromGstin) || fromGstin, source: 'gstin' }
  const fromState = stateCodeFromText(input.customerState)
  if (fromState) return { code: fromState, label: stateLabelFromCode(fromState) || String(input.customerState).trim(), source: 'state' }
  return { code: null, label: '', source: 'none' }
}

export interface GstModeInput {
  /** false means the document is deliberately without GST. */
  gstEnabled?: boolean | null
  /** Only used when the place of supply cannot be worked out; otherwise the place decides. */
  gstMode?: GstMode | null
  /** Reverse charge: the buyer pays the tax, so none is charged on the document. */
  reverseCharge?: boolean | string | null
  companyState?: string | null
  companyGstin?: string | null
  /** The customer's State field (the place of supply when the customer has no GSTIN). */
  customerState?: string | null
  customerGstin?: string | null
}

const isReverseCharge = (value: GstModeInput['reverseCharge']) =>
  typeof value === 'string' ? /^(yes|true|y)$/i.test(value.trim()) : !!value

/**
 * The GST mode for a document that charges GST.
 *  - gstEnabled false, or reverse charge: 'none'.
 *  - otherwise by place of supply: same state as the seller -> 'intra' (CGST+SGST), a different state -> 'inter' (IGST).
 *  - if the place of supply or the seller's state is unknown, an explicit 'intra' / 'inter' is kept, else 'intra'.
 *    (Documents are not issued with an unknown place of supply — see placeOfSupplyProblem.)
 */
export function resolveGstMode(input: GstModeInput): GstMode {
  if (input.gstEnabled === false) return 'none'
  if (isReverseCharge(input.reverseCharge)) return 'none'

  const seller = stateCodeFromGstin(input.companyGstin) ?? stateCodeFromText(input.companyState)
  const place = resolvePlaceOfSupply(input).code
  if (seller && place) return seller === place ? 'intra' : 'inter'
  if (input.gstMode === 'intra' || input.gstMode === 'inter') return input.gstMode
  return 'intra'
}

export const PLACE_OF_SUPPLY_REQUIRED_MESSAGE =
  'Add the customer\'s GSTIN or choose their State before issuing this document — the place of supply decides whether CGST + SGST or IGST is charged.'

/**
 * A reason this document must not be issued yet, or null. GST that is charged needs a known place of
 * supply (the customer's GSTIN or State); documents without GST, or on reverse charge, do not.
 */
export function placeOfSupplyProblem(input: GstModeInput): string | null {
  if (input.gstEnabled === false) return null
  if (isReverseCharge(input.reverseCharge)) return null
  if (input.gstMode === 'none') return null
  return resolvePlaceOfSupply(input).code ? null : PLACE_OF_SUPPLY_REQUIRED_MESSAGE
}
